import type { NutritionSnapshotRecord } from "../../persistence/records";
import { FOOD_MEASURE_LABEL_MAX_LENGTH } from "../../../domain/foods/food";
import {
  NUTRIENT_CATALOG,
  convertToPreferredNutrientUnit,
  getNutrientDefinition,
  type NutrientDefinition,
  type NutrientId,
} from "../../../domain/nutrition/nutrient-catalog";
import type { OpenFoodFactsProduct } from "./schemas";
import type { ImportedFoodBasisUnit, ImportedFoodDraft, ImportedFoodMeasure } from "./types";

export function mapOpenFoodFactsProduct(product: OpenFoodFactsProduct): ImportedFoodDraft | undefined {
  const barcode = normalizeBarcode(product.code ?? "");
  const name = cleanText(product.product_name ?? product.generic_name ?? "");
  if (!barcode || !name) return undefined;
  const nutrients = product.nutriments ?? {};
  const fibreGrams = nutrientNumber(nutrients, "fiber_100g");
  const rawMacros = {
    protein: nutrientNumber(nutrients, "proteins_100g"),
    carbohydrate: totalCarbohydrateGrams(nutrients, fibreGrams),
    fat: nutrientNumber(nutrients, "fat_100g"),
  };
  const missingMacros = (Object.entries(rawMacros) as Array<[keyof typeof rawMacros, number | undefined]>)
    .filter(([, value]) => value === undefined)
    .map(([key]) => key);
  const declaredCalories = calorieNumber(nutrients);
  const basisUnit = canonicalQuantityUnit(product);

  let macroSuggestion: ImportedFoodDraft["macroSuggestion"] = "provider";
  let proteinGrams = rawMacros.protein ?? 0;
  let carbohydrateGrams = rawMacros.carbohydrate ?? 0;
  let fatGrams = rawMacros.fat ?? 0;
  if (missingMacros.length > 0 && declaredCalories !== undefined) {
    // The requested default allocates one third of declared energy to each macro.
    // It is explicitly marked unconfirmed; the UI must not silently log it.
    proteinGrams = declaredCalories / 3 / 4;
    carbohydrateGrams = declaredCalories / 3 / 4;
    fatGrams = declaredCalories / 3 / 9;
    macroSuggestion = "equal-energy-fallback";
  } else if (missingMacros.length > 0) {
    macroSuggestion = "empty-fallback";
  }

  const nutrition: NutritionSnapshotRecord = {
    proteinMg: gramsToMilligrams(proteinGrams),
    carbohydrateMg: gramsToMilligrams(carbohydrateGrams),
    fatMg: gramsToMilligrams(fatGrams),
    ...(fibreGrams === undefined ? {} : { fibreMg: gramsToMilligrams(fibreGrams) }),
    micronutrients: mapMicronutrients(nutrients),
  };

  return {
    provider: "open-food-facts",
    externalId: barcode,
    sourceUrl: `https://world.openfoodfacts.org/product/${encodeURIComponent(barcode)}`,
    name,
    brand: cleanText(product.brands ?? "") || undefined,
    barcode,
    basis: { amount: 100, unit: basisUnit },
    measures: mapMeasures(product, basisUnit),
    nutrition,
    declaredCaloriesPer100g: declaredCalories,
    imageUrl: product.image_front_small_url ?? product.image_front_url,
    missingMacros,
    macroSuggestion,
    requiresMacroConfirmation: missingMacros.length > 0,
  };
}

function canonicalQuantityUnit(product: OpenFoodFactsProduct): ImportedFoodBasisUnit {
  const servingUnit = positiveFiniteNumber(product.serving_quantity) === undefined
    ? undefined
    : quantityUnit(product.serving_quantity_unit);
  const packageUnit = positiveFiniteNumber(product.product_quantity) === undefined
    ? undefined
    : quantityUnit(product.product_quantity_unit);
  return servingUnit ?? packageUnit ?? "g";
}

function mapMeasures(
  product: OpenFoodFactsProduct,
  basisUnit: ImportedFoodBasisUnit,
): ImportedFoodMeasure[] {
  const measures: ImportedFoodMeasure[] = [];
  const servingAmount = positiveFiniteNumber(product.serving_quantity);
  if (servingAmount !== undefined && quantityUnit(product.serving_quantity_unit) === basisUnit) {
    const servingLabel = cleanMeasureLabel(product.serving_size ?? "");
    measures.push({
      id: "serving",
      label: servingLabel ? cleanMeasureLabel(`Serving: ${servingLabel}`) : "Serving",
      amount: servingAmount,
    });
  }

  const packageAmount = positiveFiniteNumber(product.product_quantity);
  if (packageAmount !== undefined && quantityUnit(product.product_quantity_unit) === basisUnit) {
    const quantityLabel = cleanMeasureLabel(product.quantity ?? "");
    measures.push({
      id: "package",
      label: cleanMeasureLabel(quantityLabel ? `Whole package (${quantityLabel})` : "Whole package"),
      amount: packageAmount,
    });
  }
  return measures;
}

function quantityUnit(value: unknown): ImportedFoodBasisUnit | undefined {
  if (typeof value !== "string") return undefined;
  const unit = value.normalize("NFKC").trim().toLocaleLowerCase("en-AU");
  return unit === "g" || unit === "ml" ? unit : undefined;
}

function positiveFiniteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined;
}

export function normalizeBarcode(input: string): string | undefined {
  const digits = input.replace(/[\s-]/g, "");
  return /^\d{6,14}$/.test(digits) ? digits : undefined;
}

function mapMicronutrients(nutrients: Record<string, unknown>): NutritionSnapshotRecord["micronutrients"] {
  const matches = new Map<string, { definition: NutrientDefinition; grams: number; confidence: number }>();
  const ambiguousConfidence = new Map<string, number>();
  const fields = Object.keys(nutrients).filter((key) => key.endsWith("_100g")).sort();

  for (const field of fields) {
    const grams = nutrientNumber(nutrients, field);
    if (grams === undefined) continue;
    const providerMatch = matchOpenFoodFactsField(field);
    if (!providerMatch) continue;
    const { definition, confidence } = providerMatch;
    const ambiguousAt = ambiguousConfidence.get(definition.id);
    if (ambiguousAt !== undefined) {
      if (confidence <= ambiguousAt) continue;
      ambiguousConfidence.delete(definition.id);
    }

    // The catalog's documented provider fields outrank established synonyms.
    // Equal-confidence alternatives are deduplicated only when they agree. A
    // disagreement is omitted because neither field has defensible precedence.
    const existing = matches.get(definition.id);
    if (!existing || confidence > existing.confidence) {
      matches.set(definition.id, { definition, grams, confidence });
    } else if (confidence === existing.confidence && !sameProviderAmount(grams, existing.grams)) {
      matches.delete(definition.id);
      ambiguousConfidence.set(definition.id, confidence);
    }
  }

  // OFF normally computes these fields from one another. Retain a conservative
  // fallback for incomplete/legacy records using its standard salt factor:
  // salt = sodium x 2.5. An explicit sodium field always wins.
  if (!matches.has("sodium") && !ambiguousConfidence.has("sodium")) {
    const salt = nutrientNumber(nutrients, "salt_100g");
    if (salt !== undefined) {
      const definition = getNutrientDefinition("sodium")!;
      matches.set(definition.id, { definition, grams: salt / 2.5, confidence: 1 });
    }
  }

  // Open Food Facts can expose vitamin-A components rather than a total. The
  // AU/NZ NRV defines 1 mcg retinol equivalent as 1 mcg retinol, 6 mcg
  // beta-carotene, or 12 mcg of other provitamin-A carotenoids. Only use this
  // established fallback when the provider did not supply total vitamin A.
  if (!hasDocumentedProviderMatch(matches, "vitamin-a") && !ambiguousConfidence.has("vitamin-a")) {
    const retinol = nutrientNumber(nutrients, "retinol_100g");
    const betaCarotene = nutrientNumber(nutrients, "beta-carotene_100g");
    const alphaCarotene = nutrientNumber(nutrients, "alpha-carotene_100g");
    const betaCryptoxanthin = nutrientNumber(nutrients, "beta-cryptoxanthin_100g");
    if ([retinol, betaCarotene, alphaCarotene, betaCryptoxanthin].some((value) => value !== undefined)) {
      const definition = getNutrientDefinition("vitamin-a")!;
      matches.set(definition.id, {
        definition,
        grams:
          (retinol ?? 0) +
          (betaCarotene ?? 0) / 6 +
          ((alphaCarotene ?? 0) + (betaCryptoxanthin ?? 0)) / 12,
        confidence: 1,
      });
    }
  }

  const result: NutritionSnapshotRecord["micronutrients"] = {};
  for (const definition of NUTRIENT_CATALOG) {
    const match = matches.get(definition.id);
    if (!match) continue;
    const converted = convertToPreferredNutrientUnit(definition, match.grams, "g");
    result[definition.id] = converted;
  }
  return result;
}

function sameProviderAmount(left: number, right: number): boolean {
  const tolerance = Math.max(1e-12, Math.max(Math.abs(left), Math.abs(right)) * 1e-9);
  return Math.abs(left - right) <= tolerance;
}

const SAFE_PROVIDER_SYNONYMS: Readonly<Record<string, NutrientId>> = Object.freeze({
  "thiamin-100g": "vitamin-b1",
  "thiamine-100g": "vitamin-b1",
  "riboflavin-100g": "vitamin-b2",
  "niacin-100g": "vitamin-b3",
  "vitamin-b3-100g": "vitamin-b3",
  "vitamin-b5-100g": "vitamin-b5",
  "folate-100g": "vitamin-b9",
  "vitamin-b7-100g": "vitamin-b7",
  "ascorbic-acid-100g": "vitamin-c",
  "phosphorous-100g": "phosphorus",
});

function matchOpenFoodFactsField(
  field: string,
): { definition: NutrientDefinition; confidence: number } | undefined {
  const normalized = normalizeProviderField(field);
  for (const definition of NUTRIENT_CATALOG) {
    if (definition.openFoodFactsKeys.some((key) => normalizeProviderField(key) === normalized)) {
      return { definition, confidence: 2 };
    }
  }
  const synonymId = SAFE_PROVIDER_SYNONYMS[normalized];
  const definition = synonymId && getNutrientDefinition(synonymId);
  return definition ? { definition, confidence: 1 } : undefined;
}

function normalizeProviderField(field: string): string {
  return field
    .normalize("NFKC")
    .trim()
    .toLocaleLowerCase("en-AU")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function hasDocumentedProviderMatch(
  matches: ReadonlyMap<string, { confidence: number }>,
  nutrientId: string,
): boolean {
  return (matches.get(nutrientId)?.confidence ?? 0) >= 2;
}

function calorieNumber(nutrients: Record<string, unknown>): number | undefined {
  const kcal = nutrientNumber(nutrients, "energy-kcal_100g");
  if (kcal !== undefined) return kcal;
  const kilojoules = nutrientNumber(nutrients, "energy_100g");
  return kilojoules === undefined ? undefined : kilojoules / 4.184;
}

/**
 * Open Food Facts' `carbohydrates_100g` is available carbohydrate and excludes
 * fibre. The local domain stores total carbohydrate, so prefer the provider's
 * explicit gross value and otherwise add reported fibre to the available value.
 */
function totalCarbohydrateGrams(
  nutrients: Record<string, unknown>,
  fibreGrams: number | undefined,
): number | undefined {
  const grossCarbohydrate = nutrientNumber(nutrients, "carbohydrates-total_100g");
  if (grossCarbohydrate !== undefined) return grossCarbohydrate;
  const availableCarbohydrate = nutrientNumber(nutrients, "carbohydrates_100g");
  return availableCarbohydrate === undefined ? undefined : availableCarbohydrate + (fibreGrams ?? 0);
}

function nutrientNumber(nutrients: Record<string, unknown>, key: string): number | undefined {
  const value = nutrients[key];
  const parsed = typeof value === "number" ? value : typeof value === "string" ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

function gramsToMilligrams(value: number): number {
  return Math.max(0, Math.round(value * 1_000));
}

function cleanText(value: string): string {
  return value.replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim().slice(0, 500);
}

function cleanMeasureLabel(value: string): string {
  return cleanText(value).slice(0, FOOD_MEASURE_LABEL_MAX_LENGTH);
}
